import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import styled from 'styled-components';
import Loading from './Loading';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.svg';

// Goal: create a confirmation page that allows the user to connect their PG&E account
const Container = styled.div`
  max-width: 400px;
  margin: 40px auto;
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const Logo = styled.img`
  width: 200px;
  margin-bottom: 30px;
`;

const Button = styled.button`
  width: 250px;
  padding: 12px 20px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  margin: 10px 0;

  &:hover {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const NavLink = styled(Link)`
  color: #4CAF50;
  text-decoration: none;
  padding: 12px 20px;
  font-size: 16px;
  display: block;
  width: 250px;
  text-align: center;
  margin: 10px 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #ddd;
  margin: 15px 0;
`;


const ErrorMessage = styled.div`
  color: #dc3545;
  padding: 10px;
  margin: 10px 0;
  border: 1px solid #dc3545;
  border-radius: 4px;
  background-color: #fff;
`;

// Modal components for confirmation dialogs
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalMessage = styled.p`
  font-size: 16px;
  color: #333;
  margin-bottom: 25px;
  line-height: 1.5;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  min-width: 80px;
  
  &.confirm {
    background-color: #4CAF50;
    color: white;
    
    &:hover {
      background-color: #45a049;
    }
  }
  
  &.cancel {
    background-color: #6c757d;
    color: white;
    
    &:hover {
      background-color: #5a6268;
    }
  }
`;

function Confirmation() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalAction, setModalAction] = useState(null);

  const handleConnectPGE = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Checking token status...');
      
      // Step 1: Check user's token status
      const tokenStatus = await userService.checkTokenStatus();
      console.log('Token status:', tokenStatus);
      
      // Step 2: Decision tree based on token status
      if (!tokenStatus.hasRefreshToken) {
        // No refresh token exists
        setModalMessage('Proceed to PG&E account login.');
        setModalAction(() => startOAuthFlow);
        setShowModal(true);
      } else if (tokenStatus.refreshTokenExpired) {
        // Refresh token exists but expired
        setModalMessage('Refresh_token expired. Login to PG&E account again.');
        setModalAction(() => startOAuthFlow);
        setShowModal(true);
      } else {
        // Refresh token exists and valid
        if (tokenStatus.hasAccessToken) {
          // Access token exists
          setModalMessage('We have your PG&E information. Proceeding to Dashboard');
          setModalAction(() => () => navigate('/dashboard'));
          setShowModal(true);
        } else {
          // Access token doesn't exist (shouldn't happen)
          setError('Access token not found. Please try connecting again.');
        }
      }
      
    } catch (error) {
      console.error('Failed to check token status:', error);
      setError('Failed to check PG&E connection status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startOAuthFlow = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Starting OAuth flow...');
      
      const result = await userService.initiateOAuth();
      
      // Check if user already has valid tokens
      if (result && result.hasTokens) {
        console.log('User already has valid PG&E access tokens');
        setError(null);
        // Navigate to dashboard since user is already connected
        navigate('/dashboard');
        return;
      }
      
      // If redirecting to OAuth, the userService will handle the redirect
      // and we don't need to do anything else here
      if (result && result.redirecting) {
        console.log('Redirecting to PG&E authorization...');
        // The redirect will happen in userService, so we just wait
        return;
      }
      
    } catch (error) {
      console.error('Failed to start OAuth flow:', error);
      setError('Failed to connect to PG&E. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalConfirm = () => {
    setShowModal(false);
    if (modalAction) {
      modalAction();
    }
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setModalAction(null);
  };

  if (isLoading) {
    return (
      <Container>
        <Loading message="Connecting to PG&E..." />
      </Container>
    );
  }
  

  return (
    <>
      <Container>
        <Logo src={logo} alt="Logo" />
        
        <h2>Welcome!</h2>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Button onClick={handleConnectPGE}>
          Connect PG&E Account
        </Button>

        <Divider />
        
        <NavLink to="/user-info">
          User Information Panel
        </NavLink>
        
        <NavLink to="/about">
          About Us
        </NavLink>
      </Container>

      {/* Confirmation Modal */}
      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalMessage>{modalMessage}</ModalMessage>
            <ModalButtonContainer>
              <ModalButton className="confirm" onClick={handleModalConfirm}>
                Confirm
              </ModalButton>
              <ModalButton className="cancel" onClick={handleModalCancel}>
                Cancel
              </ModalButton>
            </ModalButtonContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

export default Confirmation;